/* eslint-disable react-hooks/exhaustive-deps */
import { memo, useEffect, useState } from 'react'
import Link from 'next/link'

import VetsResult from './VetsResult'
import FeatureVets from './FeatureVets'

import { GET_VETERINARIANS } from '@/graphql/index'
import { slugConverter } from '@/utils/converter'
import query from '@/utils/query'
import { ICitiesByState, ICity, IState } from '@/utils/countries_and_states'

import styles from './searchResult.module.scss'

const States = memo(
  ({ location, countryName }: { location: IState[]; countryName?: string }) => {
    return (
      <>
        {countryName && <h3>{countryName}</h3>}
        <div className={styles.breedStates}>
          {location.map((l, index) => (
            <div key={index} className={styles.match}>
              <Link
                href={`/find-a-vet/${l.state_code.toLowerCase()}`}
                className={styles.name}
              >
                {l.name}
              </Link>
            </div>
          ))}
        </div>
      </>
    )
  }
)

const RelatedStates = memo(
  ({
    locations,
    inputText,
  }: {
    locations: ICitiesByState[]
    inputText: string
  }) => {
    const [usa, setUsa] = useState(locations[1].states)
    const [canada, setCanada] = useState(locations[0].states)

    useEffect(() => {
      if (inputText.length === 0) {
        setUsa(locations[1].states)
        setCanada(locations[0].states)
      }
      if (inputText.length > 0) {
        setUsa(
          locations[1].states.filter((state) =>
            state.name.toLowerCase().includes(inputText)
          )
        )
        setCanada(
          locations[0].states.filter((state) =>
            state.name.toLowerCase().includes(inputText)
          )
        )
      }
    }, [inputText])

    return (
      <div className={styles.relatedStatesContainer}>
        {usa.length === 0 && canada.length === 0 && (
          <div className={styles.noResult}>
            <p>
              We have no locations that match this description, please search
              again!
            </p>
          </div>
        )}

        <States location={usa} />
        <States location={canada} countryName="Canada" />
      </div>
    )
  }
)

const Cities = memo(
  ({ location, state }: { location: ICity[]; state?: IState }) => {
    const stateName = state?.state_code

    return (
      <>
        <div className={styles.breedStates}>
          {location?.map((l, index) => (
            <div key={index} className={styles.match}>
              <Link
                href={`/find-a-vet/${stateName.toLowerCase()}/${slugConverter(l.name).toLowerCase()}`}
                className={styles.name}
              >
                {l.name}
              </Link>
            </div>
          ))}
        </div>
      </>
    )
  }
)

const RelatedCities = memo(
  ({ states, inputText }: { states?: IState; inputText: string }) => {
    const [cities, setCities] = useState(states?.cities)
    const [showAll, setShowAll] = useState(false)
    const isLarge = cities.length > 50
    const firstCities = cities.slice(0, 50)

    // Check if Javascript is fully loaded
    const [hasLoaded, setHasLoaded] = useState(false)
    useEffect(() => {
      setHasLoaded(true)
    })

    useEffect(() => {
      setCities(states?.cities)
    }, [states])

    useEffect(() => {
      if (inputText.length === 0) {
        setCities(states?.cities)
      }
      if (inputText.length > 0) {
        setCities(
          states.cities.filter((city) =>
            city.name.toLowerCase().includes(inputText)
          )
        )
      }
    }, [inputText])

    return (
      <div className={styles.relatedStatesContainer}>
        {cities?.length === 0 && (
          <div className={styles.noResult}>
            <p>
              We have no locations that match this description, please search
              again!
            </p>
          </div>
        )}

        <Cities
          state={states}
          location={!hasLoaded ? cities : showAll ? cities : firstCities}
        />
        {isLarge && hasLoaded && (
          <div
            onClick={() => setShowAll((curr) => !curr)}
            className={styles.view}
          >
            <p>
              {showAll ? 'View less' : 'View more'}{' '}
              <span>
                <img
                  width="11px"
                  height="6px"
                  alt={showAll ? 'Black up arrow' : 'Black down arrow'}
                  src={
                    showAll
                      ? '/search/arrow-top-black.svg'
                      : '/search/arrow-down-black.svg'
                  }
                />
              </span>
            </p>
          </div>
        )}
      </div>
    )
  }
)

interface ISearchResult {
  state?: string
  city?: string
  citiesByState?: ICitiesByState[]
}

export default function SearchResult({
  state,
  city,
  citiesByState,
}: ISearchResult) {
  const data = citiesByState

  const dataState =
    data[1].states.filter((elem) => elem.state_code === state).length > 0
      ? data[1].states.filter((elem) => elem.state_code === state)
      : data[0].states.filter((elem) => elem.state_code === state)

  const [inputText, setInputText] = useState('')
  const [result, setResult] = useState([])
  const [loading, setLoading] = useState<boolean>(false)

  const inputHandler = (e) => {
    const lowerCase = e.target.value.toLowerCase()
    setInputText(lowerCase)
  }

  const location = `${city}, ${state}`

  const fetchVets = async () => {
    setLoading(true)
    const { getVeterinarians } = await query({
      query: GET_VETERINARIANS,
      variables: {
        location,
      },
    })

    setResult(getVeterinarians)
    setLoading(false)
  }

  useEffect(() => {
    fetchVets()
  }, [state && city])

  const vetsData = result?.filter((elem) => elem.location.city === city)

  return (
    <div className={styles.article}>
      {state && (
        <>
          <div className={styles.breadcrumbs}>
            {state && !city ? (
              <div className={styles.steps}>
                {' '}
                <a
                  className={styles.step}
                  href={`${process.env.NEXT_PUBLIC_ROOT_URL}/find-a-vet`}
                >
                  Find a Vet{' '}
                </a>
                <span className={styles.arrowRight} />
                <div className={styles.step}>{dataState[0]?.name} </div>
              </div>
            ) : (
              <div className={styles.steps}>
                <a
                  className={styles.step}
                  href={`${process.env.NEXT_PUBLIC_ROOT_URL}/find-a-vet`}
                >
                  Find a Vet{' '}
                </a>
                <span className={styles.arrowRight} />
                <a
                  className={styles.step}
                  href={`${process.env.NEXT_PUBLIC_ROOT_URL}/find-a-vet/${state.toLowerCase()}`}
                >
                  {dataState[0]?.name}{' '}
                </a>
                <span className={styles.arrowRight} />{' '}
                <div className={styles.step}>{city} </div>
              </div>
            )}
          </div>
          <div className={styles.titleContainer}>
            <h2 className={styles.mainTitle}>
              {state && !city
                ? `Vets in ${dataState[0]?.name} State`
                : `Vets in ${city}, ${state}`}{' '}
            </h2>
            {state && city && <span>({vetsData?.length} vets)</span>}
          </div>
        </>
      )}
      {state && !city && (
        <FeatureVets slug={state} state={dataState[0]?.name} />
      )}
      {!city && (
        <div className={styles.titleWrapper}>
          <h3 className={styles.title}>
            {' '}
            {state && !city
              ? 'Explore Vets by City'
              : 'Explore Vets by State or Province'}
          </h3>
          <div className={styles.field}>
            <input type="search" onChange={inputHandler} />
            <div className={styles.icon} />
            {inputText.length === 0 && (
              <span>
                {state ? 'Search for City' : 'Search State or Province'}
              </span>
            )}
          </div>
        </div>
      )}

      {!!data && !state && (
        <RelatedStates locations={data} inputText={inputText} />
      )}
      {state && !city && (
        <RelatedCities states={dataState[0]} inputText={inputText} />
      )}
      {state && city && (
        <VetsResult
          vetsData={vetsData}
          loading={loading}
          city={city}
          state={state}
        />
      )}
    </div>
  )
}
